import React from 'react';
import styles from './ContactCard.module.scss';
import classNames from 'classnames';
import { ButtonStandard } from '@src/components/Button';
import Heading from '@src/elements/Heading';
import { Location } from '@src/graphql/gql-types';
import Visibility from '@src/elements/Visibility';
import { COLOR_OUTERSPACE } from '@src/types/colors';
import BoxIcon from '@src/elements/BoxIcon';
import { ButtonLinkStandard } from '@src/components/ButtonLink';
import { useViewport } from '@src/hooks/useViewport';
import { handleEnquireButtonScrollToClick } from '@src/utils/scrollToEnquireForm';

type ContactCard = {
  name: string;
  image: string;
  position?: string | undefined;
  email?: string | undefined;
  phoneNumber?: string | undefined;
  variant?: string | undefined;
  location?: Location;
  showImageOnMobile?: boolean;
};
interface ContactButton {
  isLink: boolean;
  boxIcon?: string;
  text: string;
  href?: string;
  openNewWindow?: boolean;
  label?: string;
  onClick?: () => void;
}

const ContactButton: React.FC<ContactButton> = ({
  isLink,
  boxIcon,
  text,
  href,
  openNewWindow = false,
  label,
  onClick
}) =>
  isLink ? (
    <ButtonLinkStandard
      color={COLOR_OUTERSPACE}
      fillVariant="outline"
      boxIcon="right-arrow-alt"
      iconPosition="right"
      size="sm"
      aria-label={label ?? ''}
      href={href}
      openNewWindow={openNewWindow}
    >
      <div className={styles.buttonContents}>
        <div className={styles.buttonIcon}>
          {boxIcon && (
            <BoxIcon name={boxIcon} size="xs" className={styles.boxIcon} />
          )}
        </div>
        <span>{text}</span>
      </div>
    </ButtonLinkStandard>
  ) : (
    <ButtonStandard
      color={COLOR_OUTERSPACE}
      fillVariant="outline"
      boxIcon="right-arrow-alt"
      iconPosition="right"
      size="sm"
      aria-label={label ?? ''}
      onClick={onClick}
    >
      <div className={styles.buttonContents}>
        <div className={styles.buttonIcon}>
          {boxIcon && (
            <BoxIcon name={boxIcon} size="xs" className={styles.boxIcon} />
          )}
        </div>
        <span>{text}</span>
      </div>
    </ButtonStandard>
  );

const ContactCard: React.FC<ContactCard> = ({
  name,
  position,
  phoneNumber,
  email,
  image,
  variant,
  location,
  showImageOnMobile = true
}) => {
  const emailLink = email ? `mailto:${email}` : '';
  const phoneLink = phoneNumber ? `tel:${phoneNumber}` : '';

  const { width } = useViewport();

  const getDirectionsURL = (lat: number, lon: number) => {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat}%2C${lon}`;
  };

  return (
    <div
      className={classNames(styles.contactCardContainer, {
        [styles.propertyVariant]: variant === 'property'
      })}
    >
      <Visibility
        xs={showImageOnMobile}
        sm={showImageOnMobile}
        md
        lg
        xl
        xxl
        xxxl
      >
        <div className={styles.contactCardImage}>
          <picture>
            <source srcSet={image} type="image/webp" />
            <img src={image} alt={name} />
          </picture>
        </div>
      </Visibility>

      <div className={styles.contactCardContentWrapper}>
        {name && (
          <div className={styles.contactCardMetadata}>
            {variant === 'property' ? (
              <Heading priority={4} fontStyle="h4">
                {name}
              </Heading>
            ) : (
              <p className={styles.contactCardName}>{name}</p>
            )}
            {position && (
              <p className={styles.contactCardPosition}>{position}</p>
            )}
          </div>
        )}

        <div className={styles.contactCardCtas}>
          {phoneLink && (
            <ContactButton
              href={phoneLink}
              text={phoneNumber ?? ''}
              boxIcon="phone"
              isLink={true}
            />
          )}

          {emailLink && (
            <ContactButton
              href={emailLink}
              text="Send an email"
              boxIcon="envelope"
              isLink={true}
            />
          )}

          {location && location.lat && location.lon && (
            <ContactButton
              href={getDirectionsURL(location.lat, location.lon)}
              text="Get directions"
              boxIcon="map"
              isLink={true}
            />
          )}

          {variant === 'property' && (
            <ContactButton
              onClick={() => handleEnquireButtonScrollToClick(width)}
              text="Enquire online"
              boxIcon="at"
              isLink={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactCard;
